<template>
  <AppLink
    :to="route_to"
    @click="handleClick"
  >
    <div
      v-if="notification"
      class="p-3 w-full flex gap-2 cursor-pointer"
      :class="{
        'bg-gray-100': _.isNil(notification.attributes.read_at),
      }"
      :data-notifiable-resource-id="data_notifiable_resource_id"
      :data-notifiable-resource-type="data_notifiable_resource_type"
      data-test="notification-item"
    >
      <figure>
        <avatar
          v-if="notification.attributes.notifier_name"
          size="40"
          :src="notification.attributes.notifier_avatar_url"
          :username="notification.attributes.notifier_name"
        />
        <div
          v-else
          class="flex items-center justify-center bg-primary-500 text-white size-10 rounded-full"
        >
          <i :class="activity_icon" />
        </div>
      </figure>

      <div class="flex flex-1 flex-col">
        <div class="text-sm mb-2">
          <FontAwesomeIcon
            v-if="notification.attributes.opened_at === null"
            class="text-primary-500 text-xs mr-2"
            data-test="unread-notification"
            :icon="['fa', 'circle']"
          />

          <span
            v-if="notification.attributes.notifier_name"
            class="font-semibold pr-1 text-sm"
          >
            {{ $truncate(notification.attributes.notifier_name, 35) }}
          </span>
          <span
            v-if="notification_subgroup.attributes.notifiers_count > 1"
            class="font-semibold pr-1 text-sm"
          >
            {{ $t('notification.other_users', notification_subgroup.attributes.notifiers_count - 1) }}
          </span>
          <span
            class="text-sm"
            data-test="notification-text"
            v-html="notification_text"
          />
        </div>
        <div class="text-gray-500 text-xs flex gap-1 flex-wrap">
          <span>{{ created_at }}</span>
          <span
            v-if="notification.attributes.project_id && project_name"
            class="capitalize"
          >
            &bull;
            {{ $truncate(project_name, 30) }}
          </span>
          <span
            v-if="level_name"
            class="capitalize"
          >
            &bull;
            {{ $truncate(level_name, 30) }}
          </span>
        </div>
      </div>

      <div class="flex gap-2 items-center flex-col sm:flex-row">
        <a
          v-tooltip.left="
            $t(`notification.actions.mark_as_${_.isNil(notification.attributes.read_at) ? 'read' : 'unread'}`)
          "
          :data-test="`toggle-notification-${_.isNil(notification.attributes.read_at) ? 'read' : 'unread'}`"
          @click.stop.prevent="changeReadStatus(notification_subgroup)"
        >
          <FontAwesomeIcon
            v-if="_.isNil(notification.attributes.read_at)"
            class="text-gray-500 hover:text-gray-800 transition-colors"
            :icon="['far', 'eye']"
          />
          <FontAwesomeIcon
            v-else
            class="text-gray-400 hover:text-gray-600 transition-colors"
            :icon="['far', 'eye-slash']"
          />
        </a>
        <a>
          <FontAwesomeIcon
            v-tooltip.left="$t('notification.actions.delete')"
            class="text-gray-500 hover:text-gray-800 transition-colors"
            data-test="delete-notification"
            :icon="['far', 'circle-xmark']"
            @click.stop.prevent="$emit('onDelete')"
          />
        </a>
      </div>
    </div>
  </AppLink>
</template>

<script setup>
import { computed, inject, ref } from 'vue';
import _ from 'lodash-es';
import { useStore } from 'vuex';
import { PROFILE_TYPES, RECORD_TYPE } from '@/app/data/model_constants';
import { ACTIVITIES_ICONS } from '@/app/data/activities_constants';
import { NOTIFICATIONS_CUSTOMERS } from '@/app/data/notifications_constants';
import { NOTIFICATION_GROUP_TO_ROUTE, NOTIFICATIONS_TO_ROUTES } from '@/app/data/notifications_routes';
import { distanceInWordsToNowWithLocale } from '@/app/util/date_fns';
import useActivityNotificationItem from '@/app/composables/useActivityNotificationItem';
import { useI18n } from 'vue-i18n';
import { useToasts } from '@/app/util/toasts';

const props = defineProps({
  is_dashboard: { type: Boolean, default: false },
  notification_subgroup: { type: Object, required: true },
});

const emit = defineEmits(['onChangeReadStatus', 'onDelete', 'click']);

const store = useStore();
const { t } = useI18n();
const emitter = inject('emitter');
const { toast_action_failed, toast_action_succeeded } = useToasts();

const is_fetching = ref(false);

const notifications = computed(() => store.getters.notifications);
const current_profile = computed(() => store.getters.current_profile);

const notification = computed(() =>
  _.find(notifications.value, ['id', props.notification_subgroup.attributes.notification_id]),
);

const { project_name, project_sup } = useActivityNotificationItem(notification);

const data_notifiable_resource_id = computed(() => {
  switch (notification.value.attributes.notifiable_type) {
    case RECORD_TYPE.DOCUMENT:
      return notification.value.attributes.parameters.attachment_id;
    case RECORD_TYPE.ANNOUNCEMENT_LEVEL:
      return notification.value.attributes.parameters.announcement_id;
    default:
      return notification.value.attributes.notifiable_id;
  }
});

const data_notifiable_resource_type = computed(() => {
  switch (notification.value.attributes.notifiable_type) {
    case RECORD_TYPE.DOCUMENT:
      return RECORD_TYPE.ATTACHMENT;
    case RECORD_TYPE.ANNOUNCEMENT_LEVEL:
      return RECORD_TYPE.ANNOUNCEMENT;
    default:
      return notification.value.attributes.notifiable_type;
  }
});

const created_at = computed(() => distanceInWordsToNowWithLocale(props.notification_subgroup.attributes.updated_at));

const activity_icon = computed(() => {
  if (!notification.value) return 'far fa-bell fa-lg';
  const { notifiable_type } = notification.value.attributes;
  return _.find(ACTIVITIES_ICONS, ['notifiable_type', notifiable_type]).iconClass;
});

const route_notification = computed(() => NOTIFICATIONS_TO_ROUTES[notification.value.attributes.key]);

const route_notification_group = computed(() => NOTIFICATION_GROUP_TO_ROUTE[notification.value.attributes.key]);

const is_survey_notification = computed(
  () => notification.value.attributes.key === NOTIFICATIONS_CUSTOMERS.SURVEY_PUBLISHED,
);

const is_survey_answered = computed(
  () => current_profile.value.type === PROFILE_TYPES.CUSTOMER && notification.value.attributes.opened_at !== null,
);

const level_name = computed(() => {
  if (
    notification.value.attributes.group_notification_count > 1 &&
    route_notification.value !== route_notification_group.value
  )
    return null;
  if (project_sup.value) return null;
  return notification.value.attributes.level_name;
});

const notification_text = computed(() => {
  const { notifications_count, notifiers_count } = props.notification_subgroup.attributes;

  const notification_params = {
    notifiable_name: notification.value.attributes.notifiable_name,
    group_notification_count: notifications_count,
    group_name: notification.value.attributes.group_name,
    customer_group_name: notification.value.attributes.customer_group_name,
    level_name: _.get(notification.value, 'attributes.level_name', null),
  };

  if (_.get(notification.value, 'attributes.parameters.customer_status')) {
    if (notification.value.attributes.key.includes('customers.')) {
      notification_params.status = t(
        `issue.attributes.statuses.${notification.value.attributes.parameters.customer_status}`,
      );
    } else {
      notification_params.status = notification.value.attributes.parameters.status;
    }
  }

  if (
    _.get(notification.value, 'attributes.parameters.status') &&
    !notification.value.attributes.key.includes('issue.')
  ) {
    notification_params.status = t(`installment.labels.statuses.${notification.value.attributes.parameters.status}`);
  }

  let choice;
  if (notifiers_count === 1 && notifications_count === 1) {
    choice = 0;
  } else if (notifiers_count === 1 && notifications_count > 1) {
    choice = 1;
  } else if (notifiers_count > 1 && notifications_count === 1) {
    choice = 2;
  } else {
    choice = 3;
  }

  let notification_text = t(notification.value.attributes.key, notification_params, choice);
  if (notification_text === '_') {
    notification_text = t(notification.value.attributes.key, notification_params, choice - 1);
  }
  return notification_text;
});

const route_to = computed(() => {
  const name =
    props.notification_subgroup.attributes.notifications_count > 1
      ? route_notification_group.value
      : route_notification.value;
  const params = _.omitBy(
    {
      project_id: notification.value.attributes.project_id,
      level_id: notification.value.attributes.level_id,
      level_type: notification.value.attributes.level_type,
      ...notification.value.attributes.parameters,
    },
    _.isNil,
  );

  const query = {};
  if (params.attachment_id) {
    query.attachment_id = params.attachment_id;
    delete params.attachment_id;
  }

  return name ? { name, params, query } : {};
});

const OPEN_NOTIFICATION_SUBGROUP = (payload) => store.dispatch('OPEN_NOTIFICATION_SUBGROUP', payload);
const UNOPEN_NOTIFICATION_SUBGROUP = (payload) => store.dispatch('UNOPEN_NOTIFICATION_SUBGROUP', payload);

const changeReadStatus = async (notification_subgroup) => {
  if (is_fetching.value) return;
  const read = _.get(notification_subgroup, 'attributes.read');
  try {
    is_fetching.value = true;
    const call = read ? UNOPEN_NOTIFICATION_SUBGROUP : OPEN_NOTIFICATION_SUBGROUP;
    await call({ notification_subgroup });
    emit('onChangeReadStatus');
  } catch (e) {
    toast_action_failed();
  } finally {
    is_fetching.value = false;
  }
};

const initSurvey = () => {
  if (is_survey_answered.value) {
    toast_action_succeeded(t('survey.labels.surveyAlreadyAnswered'));
  } else {
    const { notifiable_id } = notification.value.attributes;
    emitter.emit('initSurvey', notifiable_id);
  }
};

const handleClick = () => {
  if (is_survey_notification.value) {
    initSurvey();
  }
  emit('click');
};
</script>
